import { QuestionTypes } from '@/gql/graphql';
import { fetchAllFormularies } from '@/screens/Tickets/hooks/useFetchFormularies';
import { fetchQuestions } from '@/screens/Tickets/hooks/useFetchQuestions';
import { cacheGet, cacheSet } from './groupedSelectCache';
import { usePaginationObserver } from '../misc/DialogSelect/utils';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  chakra,
  Checkbox,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { SelectionMode } from '@react-types/shared';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiSearch } from 'react-icons/bi';

import { TBaseItem } from '../misc/DialogSelect/useDialogSelect';

type SelectorProps = {
  value?: string[];
  initialOptions?: TBaseItem[];
  selectionMode?: SelectionMode;
  triggerIds?: string[];
  currentTriggerableIds?: string[];
  formularyId?: string;
  questionTypes?: QuestionTypes[];
  parentOptionId?: string;
  parentQuestionId?: string;
  onChange?: (value: TBaseItem[]) => void;
  onSelectionChange?: (keys: string[]) => void;
  [key: string]: unknown;
};

type QuestionItem = { id: string; name: string; sectionName?: string };
type FormularyState = {
  questions: QuestionItem[];
  cursor?: string;
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
};
type SearchGroup = { id: string; name: string; questions: QuestionItem[] };

const PAGE_SIZE = 20;
const SEARCH_PAGE_SIZE = 100;
const FORMULARIES_CACHE_KEY = 'all-formularies-for-questions:enabled:hasQuestions';

function QuestionRow({
  question,
  selectedIds,
  selectionMode,
  onToggle,
}: {
  question: QuestionItem;
  selectedIds: Set<string>;
  selectionMode?: SelectionMode;
  onToggle: (id: string) => void;
}) {
  return (
    <ListItem key={question.id}>
      <Checkbox
        isChecked={selectedIds.has(question.id)}
        onChange={() => onToggle(question.id)}
        size="md"
      >
        <chakra.div flexDir="column">
          <chakra.p>{question.name}</chakra.p>
          {question.sectionName && (
            <chakra.div bg="blackAlpha.50" rounded="md" p="2" mt="1">
              <chakra.p fontSize="sm">Section: {question.sectionName}</chakra.p>
            </chakra.div>
          )}
        </chakra.div>
      </Checkbox>
    </ListItem>
  );
}

function FormularyPanel({
  formularyId,
  formularyName,
  state,
  selectedIds,
  selectionMode,
  onToggleItem,
  onToggleGroup,
  onLoadMore,
}: {
  formularyId: string;
  formularyName: string;
  state: FormularyState;
  selectedIds: Set<string>;
  selectionMode?: SelectionMode;
  onToggleItem: (id: string) => void;
  onToggleGroup: (ids: string[]) => void;
  onLoadMore: (formularyId: string) => void;
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = React.useCallback(() => {
    const el = panelRef.current;
    if (!el || state.loading || !state.hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      onLoadMore(formularyId);
    }
  }, [formularyId, state.loading, state.hasMore, onLoadMore]);

  const ids = state.questions.map((q) => q.id);
  const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
  const allChecked = selectedCount === ids.length && ids.length > 0;
  const isIndeterminate = selectedCount > 0 && !allChecked;

  return (
    <AccordionItem border="none">
      {({ isExpanded }) => (
      <>
      <h2>
        <AccordionButton px="2" py="2" bg="white" position="relative" zIndex={1} {...(!isExpanded && { _focus: { boxShadow: 'none' } })}>
          <Box flex="1" textAlign="left">
            {selectionMode !== 'single' && selectionMode !== 'none' ? (
              <Checkbox
                isChecked={allChecked}
                isIndeterminate={isIndeterminate}
                onChange={() => onToggleGroup(ids)}
                onClick={(e) => e.stopPropagation()}
                fontWeight="semibold"
              >
                {formularyName}
                {state.questions.length > 0 && ` (${state.questions.length}${state.hasMore ? '+' : ''})`}
              </Checkbox>
            ) : (
              <chakra.span fontWeight="semibold" fontSize="sm">
                {formularyName}
                {state.questions.length > 0 && ` (${state.questions.length}${state.hasMore ? '+' : ''})`}
              </chakra.span>
            )}
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel ms="6" pb={4} pt={1} p="0">
        {state.loading && state.questions.length === 0 ? (
          <chakra.div w="full" py="3" display="flex" justifyContent="center">
            <Spinner size="sm" />
          </chakra.div>
        ) : state.questions.length === 0 && state.loaded ? (
          <chakra.p fontSize="sm" color="gray.400" px="4" py="2">
            Sem perguntas
          </chakra.p>
        ) : (
          <chakra.div
            ref={panelRef}
            maxH="200px"
            overflowY="auto"
            onScroll={handleScroll}
            ps="6"
            pe="2"
            pt="1"
            pb="3"
          >
            <List spacing={2}>
              {state.questions.map((question) => (
                <QuestionRow
                  key={question.id}
                  question={question}
                  selectedIds={selectedIds}
                  selectionMode={selectionMode}
                  onToggle={onToggleItem}
                />
              ))}
            </List>
            {state.loading && (
              <chakra.div w="full" py="2" display="flex" justifyContent="center">
                <Spinner size="sm" />
              </chakra.div>
            )}
          </chakra.div>
        )}
      </AccordionPanel>
      </>
      )}
    </AccordionItem>
  );
}

export function GroupedQuestionSelect({
  value,
  initialOptions,
  selectionMode,
  triggerIds,
  currentTriggerableIds,
  formularyId,
  questionTypes,
  parentOptionId,
  parentQuestionId,
  onChange,
  onSelectionChange,
}: SelectorProps) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set(value ?? []));

  // Formulary list
  const [formularies, setFormularies] = React.useState<Array<{ id: string; name: string }>>([]);
  const [formulariesLoading, setFormulariesLoading] = React.useState(false);

  // Per-formulary question state
  const [questionsByFormulary, setQuestionsByFormulary] = React.useState<
    Record<string, FormularyState>
  >({});

  // Accordion
  const [expandedIndex, setExpandedIndex] = React.useState<number>(-1);

  // Search
  const [search, setSearch] = React.useState('');
  const [searchGroups, setSearchGroups] = React.useState<SearchGroup[]>([]);
  const [searchCursor, setSearchCursor] = React.useState<string | undefined>();
  const [searchHasMore, setSearchHasMore] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);

  React.useEffect(() => {
    setSelectedIds(new Set(value ?? []));
  }, [value]);

  const fetchParams = React.useMemo(
    () => ({ triggerIds, currentTriggerableIds, formularyId, questionTypes, parentOptionId, parentQuestionId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      JSON.stringify(triggerIds),
      JSON.stringify(currentTriggerableIds),
      formularyId,
      JSON.stringify(questionTypes),
      parentOptionId,
      parentQuestionId,
    ],
  );

  // Reorder formularies on modal open: move selected question's formulary to top
  React.useEffect(() => {
    if (!isOpen) return;
    const currentSelectedId = Array.from(selectedIds)[0];
    if (!currentSelectedId) return;

    const reorderAndExpand = (fId: string) => {
      setFormularies((prev) => {
        const idx = prev.findIndex((f) => f.id === fId);
        if (idx <= 0) return prev;
        const reordered = [...prev];
        const [sel] = reordered.splice(idx, 1);
        reordered.unshift(sel);
        return reordered;
      });
      setExpandedIndex(0);
      const state = questionsByFormulary[fId];
      if (!state || (!state.loaded && !state.loading)) {
        loadQuestionsForFormulary(fId);
      }
    };

    // Check already-loaded questions first
    const foundFormularyId = Object.entries(questionsByFormulary).find(([, state]) =>
      state.questions.some((q) => q.id === currentSelectedId),
    )?.[0];

    if (foundFormularyId) {
      reorderAndExpand(foundFormularyId);
    } else {
      fetchQuestions({ questionsId: currentSelectedId, first: 1 }).then(({ items }) => {
        const fId = items[0]?.formularyId;
        if (fId) reorderAndExpand(fId);
      });
    }
  }, [isOpen]);

  // Load formularies on mount
  React.useEffect(() => {
    const cached = cacheGet<Array<{ id: string; name: string }>>(FORMULARIES_CACHE_KEY);
    if (cached) {
      setFormularies(cached);
      return;
    }
    setFormulariesLoading(true);
    fetchAllFormularies({ enabled: true, hasQuestions: true }).then((items) => {
      const sorted = items
        .filter((f) => f.name)
        .map((f) => ({ id: f.id, name: f.name! }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFormularies(sorted);
      cacheSet(FORMULARIES_CACHE_KEY, sorted);
      setFormulariesLoading(false);
    });
  }, []);

  const loadQuestionsForFormulary = React.useCallback(
    async (fId: string) => {
      setQuestionsByFormulary((prev) => {
        const cur = prev[fId];
        if (cur?.loading) return prev;
        return {
          ...prev,
          [fId]: { ...(cur ?? { questions: [], hasMore: false, loaded: false }), loading: true },
        };
      });

      const cur = questionsByFormulary[fId];
      const after = cur?.cursor;

      const { items, cursor: newCursor } = await fetchQuestions({
        first: PAGE_SIZE,
        after,
        ...fetchParams,
        formularyId: fId,
      });

      const newQuestions: QuestionItem[] = items.map((q) => ({
        id: q.id,
        name: q.name ?? '',
        sectionName: q.sectionName ?? undefined,
      }));

      setQuestionsByFormulary((prev) => {
        const existing = prev[fId]?.questions ?? [];
        return {
          ...prev,
          [fId]: {
            questions: [...existing, ...newQuestions],
            cursor: newCursor,
            hasMore: !!newCursor && newQuestions.length >= PAGE_SIZE,
            loading: false,
            loaded: true,
          },
        };
      });
    },
    [fetchParams, questionsByFormulary],
  );

  const handleAccordionChange = React.useCallback(
    (idx: number) => {
      setExpandedIndex(idx);
      const f = formularies[idx];
      if (!f) return;
      const state = questionsByFormulary[f.id];
      if (!state || (!state.loaded && !state.loading)) {
        loadQuestionsForFormulary(f.id);
      }
    },
    [formularies, questionsByFormulary, loadQuestionsForFormulary],
  );

  const handleLoadMore = React.useCallback(
    (fId: string) => {
      const state = questionsByFormulary[fId];
      if (!state || state.loading || !state.hasMore) return;
      loadQuestionsForFormulary(fId);
    },
    [questionsByFormulary, loadQuestionsForFormulary],
  );

  const mergeSearchItems = (
    prev: SearchGroup[],
    newItems: Array<{ id: string; name?: string | null; sectionName?: string | null; formularyId?: string | null; formularyName?: string | null }>,
  ): SearchGroup[] => {
    const map = new Map(prev.map((g) => [g.id, { ...g, questions: [...g.questions] }]));
    const newGroupIds: string[] = [];

    newItems.forEach((q) => {
      const groupId = q.formularyId ?? 'no_group';
      const groupName = q.formularyId ? (q.formularyName ?? '') : 'Sem formulário';
      const item: QuestionItem = { id: q.id, name: q.name ?? '', sectionName: q.sectionName ?? undefined };

      if (map.has(groupId)) {
        map.get(groupId)!.questions.push(item);
      } else {
        map.set(groupId, { id: groupId, name: groupName, questions: [item] });
        newGroupIds.push(groupId);
      }
    });

    const existing = prev.map((g) => map.get(g.id)!);
    const newer = newGroupIds.map((id) => map.get(id)!).sort((a, b) => a.name.localeCompare(b.name));
    return [...existing, ...newer];
  };

  // Search
  const searchRef = React.useRef<string>('');
  React.useEffect(() => {
    searchRef.current = search;
    if (!search) {
      setSearchGroups([]);
      setSearchCursor(undefined);
      setSearchHasMore(false);
      return;
    }

    setSearchLoading(true);
    setSearchGroups([]);
    setSearchCursor(undefined);
    setSearchHasMore(false);

    const timer = setTimeout(() => {
      if (searchRef.current !== search) return;
      fetchQuestions({ first: SEARCH_PAGE_SIZE, search, ...fetchParams }).then(
        ({ items, cursor: newCursor }) => {
          if (searchRef.current !== search) return;
          setSearchGroups(mergeSearchItems([], items));
          setSearchCursor(newCursor);
          setSearchHasMore(!!newCursor);
          setSearchLoading(false);
        },
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const loadMoreSearch = React.useCallback(async () => {
    if (!searchHasMore || searchLoading || !searchCursor) return;
    setSearchLoading(true);
    const { items, cursor: newCursor } = await fetchQuestions({
      first: SEARCH_PAGE_SIZE,
      search,
      after: searchCursor,
      ...fetchParams,
    });
    setSearchGroups((prev) => mergeSearchItems(prev, items));
    setSearchCursor(newCursor);
    setSearchHasMore(!!newCursor);
    setSearchLoading(false);
  }, [searchHasMore, searchLoading, searchCursor, search, fetchParams]);

  const { observerTarget } = usePaginationObserver({ onPaginate: loadMoreSearch });

  const toggleItem = React.useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (selectionMode === 'single' || selectionMode === 'none') {
          next.clear();
          next.add(id);
        } else if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [selectionMode],
  );

  const toggleGroup = React.useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const allLoadedQuestions = React.useMemo(
    () => Object.values(questionsByFormulary).flatMap((s) => s.questions),
    [questionsByFormulary],
  );

  const allSearchQuestions = React.useMemo(
    () => searchGroups.flatMap((g) => g.questions),
    [searchGroups],
  );

  const selectedItems = React.useMemo(() => {
    const pool = [
      ...allLoadedQuestions,
      ...allSearchQuestions,
      ...(initialOptions ?? []).map((o) => ({ id: o.id, name: o.name, sectionName: undefined })),
    ];
    const seen = new Set<string>();
    const unique = pool.filter((q) => {
      if (seen.has(q.id)) return false;
      seen.add(q.id);
      return true;
    });
    return Array.from(selectedIds)
      .map((id) => unique.find((q) => q.id === id))
      .filter((q): q is QuestionItem => !!q);
  }, [selectedIds, allLoadedQuestions, allSearchQuestions, initialOptions]);

  const handleConfirm = () => {
    const result: TBaseItem[] = selectedItems.map((q) => ({
      id: q.id,
      name: q.name,
      data: {},
    }));
    onChange?.(result);
    onSelectionChange?.(Array.from(selectedIds));
    onClose();
  };

  return (
    <>
      <Button
        onClick={onOpen}
        display="flex"
        fontWeight="normal"
        size="md"
        variant="outline"
        alignItems="center"
        justifyContent="space-between"
        flexDir="row"
        width="full"
        whiteSpace="nowrap"
        position="relative"
        overflow="hidden"
        ps="2"
        pe="3"
      >
        <chakra.span
          wordBreak="break-all"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
        >
          {selectedItems.length
            ? selectedItems.map((q) => q.name).join(', ')
            : t('placeholders.select_ellipsis')}
        </chakra.span>
        <chakra.span w="4">
          <Icon as={ChevronDownIcon} fontSize="2xl" />
        </chakra.span>
      </Button>

      <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('selects.question')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody p="0">
            <chakra.div
              w="full"
              p="6"
              maxH="500px"
              minH="300px"
              display="flex"
              gap="4"
              flexDir="column"
              overflowY="hidden"
            >
              <InputGroup>
                <Input
                  placeholder={t('placeholders.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  backgroundColor="white"
                />
                <InputRightElement>
                  <BiSearch color="#E2E8F0" />
                </InputRightElement>
              </InputGroup>

              <chakra.div overflowY="auto" flex="1">
                {formulariesLoading && !search ? (
                  <chakra.div w="full" py="4" display="flex" justifyContent="center">
                    <Spinner />
                  </chakra.div>
                ) : search ? (
                  <>
                    {searchLoading && searchGroups.length === 0 ? (
                      <chakra.div w="full" py="4" display="flex" justifyContent="center">
                        <Spinner />
                      </chakra.div>
                    ) : (
                      <>
                        {searchGroups.length === 0 && !searchLoading ? (
                          <chakra.p fontSize="sm" color="gray.400" px="2" py="2">
                            Nenhuma pergunta encontrada
                          </chakra.p>
                        ) : (
                          <Accordion allowMultiple defaultIndex={searchGroups.map((_, i) => i)}>
                            {searchGroups.map((group) => {
                              const ids = group.questions.map((q) => q.id);
                              const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
                              const allChecked = selectedCount === ids.length && ids.length > 0;
                              const isIndeterminate = selectedCount > 0 && !allChecked;
                              return (
                                <AccordionItem key={group.id} border="none">
                                  <h2>
                                    <AccordionButton px="2" py="2">
                                      <Box flex="1" textAlign="left">
                                        {selectionMode !== 'single' && selectionMode !== 'none' ? (
                                          <Checkbox
                                            isChecked={allChecked}
                                            isIndeterminate={isIndeterminate}
                                            onChange={() => toggleGroup(ids)}
                                            onClick={(e) => e.stopPropagation()}
                                            fontWeight="semibold"
                                          >
                                            {group.name} ({group.questions.length})
                                          </Checkbox>
                                        ) : (
                                          <chakra.span fontWeight="semibold" fontSize="sm">
                                            {group.name} ({group.questions.length})
                                          </chakra.span>
                                        )}
                                      </Box>
                                      <AccordionIcon />
                                    </AccordionButton>
                                  </h2>
                                  <AccordionPanel ms="6" pb={4} pt={1}>
                                    <List spacing={2}>
                                      {group.questions.map((question) => (
                                        <QuestionRow
                                          key={question.id}
                                          question={question}
                                          selectedIds={selectedIds}
                                          selectionMode={selectionMode}
                                          onToggle={toggleItem}
                                        />
                                      ))}
                                    </List>
                                  </AccordionPanel>
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        )}
                        {searchHasMore && <chakra.div ref={observerTarget} />}
                        {searchLoading && searchGroups.length > 0 && (
                          <chakra.div w="full" py="2" display="flex" justifyContent="center">
                            <Spinner size="sm" />
                          </chakra.div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <Accordion
                    allowToggle
                    index={expandedIndex}
                    onChange={(idx) => handleAccordionChange(idx as number)}
                  >
                    {formularies.map((f) => (
                      <FormularyPanel
                        key={f.id}
                        formularyId={f.id}
                        formularyName={f.name}
                        state={
                          questionsByFormulary[f.id] ?? {
                            questions: [],
                            hasMore: false,
                            loading: false,
                            loaded: false,
                          }
                        }
                        selectedIds={selectedIds}
                        selectionMode={selectionMode}
                        onToggleItem={toggleItem}
                        onToggleGroup={toggleGroup}
                        onLoadMore={handleLoadMore}
                      />
                    ))}
                  </Accordion>
                )}
              </chakra.div>
            </chakra.div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="solid"
              colorScheme="brand"
              w="full"
              size="md"
              onClick={handleConfirm}
            >
              {t('common.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
